import { env } from 'lib/env';
import { logError, logWarning, makeLoggableError } from 'lib/util/errors';
import { getAllWithPrefix } from 'lib/util/urls';
import {
  DefaultRouteQueryParameters,
  RouteQueryParameters,
  RouteState,
  TcDirectoryQueryParameters,
  TypedRouteState,
  NmdpUiTcDirectorySearchState,
  NmdpUiIndexState,
} from './SearchTypes';
import { StateMapping } from 'instantsearch.js';
import algoliaRouter from './AlgoliaRouter';
import { DeepPartial } from '@apollo/client/utilities';
import { RouterProps } from 'instantsearch.js/es/middlewares';

/**
 * Normalizes a given string for use in a route by trimming whitespace.
 * If the input is undefined or results in an empty string after trimming,
 * the function returns undefined.
 *
 * @param x - The string to normalize, or undefined.
 * @returns The trimmed string if it has a non-zero length, otherwise undefined.
 */
const normalizeForRoute = (x: string | undefined): string | undefined => {
  if (!x) {
    return undefined;
  }
  const normalized = x.trim();
  return normalized.length > 0 ? normalized : undefined;
};

/**
 * Normalizes an array of refinement values into a single string for use in routing.
 *
 * @param x - An array of strings representing refinement values, or undefined.
 * @returns A string where the refinement values are joined by a pipe ('|') character,
 *          or undefined if the input is undefined.
 */
const normalizeRefinementForRoute = (x: string[] | undefined): string | undefined =>
  x ? x.join('|') : undefined;

const splitRouteRefinement = (x?: string): string[] | undefined => (x ? x.split('|') : undefined);

const getUtmParamsFromCurrentLocation = (): Record<string, string> => {
  const currentSearch = globalThis.window?.location?.search;
  if (currentSearch === undefined || currentSearch === '') {
    return {};
  }

  return getAllWithPrefix(new URLSearchParams(currentSearch), 'utm_');
};

/**
 * Filter string specific to TC Directory search state.
 */
const FILTERS_TCDIRECTORY = 'type:tclisting';

/**
 * Represents the properties for mapping state to route parameters.
 *
 * @property uiState - The current UI state from Algolia.
 * @property addSortInfoToRoute - A flag indicating whether to include sort information in the route.
 */
type StateToRouteProps = {
  uiState: RouteState;
  addSortInfoToRoute: boolean;
};

/**
 * Represents the properties for mapping route parameters to state.
 *
 * @template TParams - The type of route query parameters.
 * @property routeState - The state object from the route, containing query parameters and other state information.
 * @property addSortInfoToRoute - A flag indicating whether to include sort information in the route.
 */
type RouteToStateProps<TParams extends RouteQueryParameters = RouteQueryParameters> = {
  routeState: TParams;
  addSortInfoToRoute: boolean;
  defaultSortByValue?: string;
  defaultSortBySuffix?: string;
  sortIndex?: string;
};

type RouteMapperOptions = {
  addSortInfoToRoute: boolean;
  defaultSortByValue?: string;
  defaultSortBySuffix?: string;
  sortIndex?: string;
  useTcDirectoryRouteState?: boolean;
};

const resolveDefaultSortByValue = ({
  defaultSortByValue,
  defaultSortBySuffix,
  sortIndex,
}: Pick<RouteMapperOptions, 'defaultSortByValue' | 'defaultSortBySuffix' | 'sortIndex'>):
  | string
  | undefined => {
  const normalizedDefaultSortByValue = normalizeForRoute(defaultSortByValue)?.toLowerCase();

  if (normalizedDefaultSortByValue) {
    return normalizedDefaultSortByValue;
  }

  const normalizedSuffix = normalizeForRoute(defaultSortBySuffix ?? sortIndex)?.toLowerCase();
  return normalizedSuffix ? `${env.ALGOLIA_INDEX_NAME}_${normalizedSuffix}` : undefined;
};

/**
 * Normalizes the index name for use in the application state.
 *
 * @param sortByProp - The sortBy property or route query parameters.
 * @returns A normalized index name string.
 */
const normalizeIndexNameForState = (
  sortByProp: string | RouteQueryParameters,
  defaultSortByValue?: string
) => {
  try {
    let sortBy = typeof sortByProp === 'string' ? sortByProp : (sortByProp.sortBy as string);
    if (sortBy?.length) {
      const normalSortBy = sortBy.toLocaleLowerCase();
      // If it starts with the search index return as is.
      if (sortBy.startsWith(env.ALGOLIA_INDEX_NAME)) {
        sortBy = normalSortBy;
      } else if (
        env.ALGOLIA_REPORTS_INDEX_NAME?.length &&
        normalSortBy.startsWith(env.ALGOLIA_REPORTS_INDEX_NAME)
      ) {
        // If it starts with the reports index name return it as-is
        sortBy = normalSortBy;
      } else {
        sortBy = `${env.ALGOLIA_INDEX_NAME}_${normalSortBy}`;
      }
    } else if (defaultSortByValue) {
      sortBy = defaultSortByValue;
    } else {
      // Default index is the search index
      sortBy = env.ALGOLIA_INDEX_NAME;
    }
    return sortBy ?? env.ALGOLIA_INDEX_NAME; // Default to the main index if sortBy is undefined
  } catch (error) {
    // If we can't normalize the index name, just return the default index
    logError('AlgoliaSearch::routeToState', makeLoggableError({ error }));
    return env.ALGOLIA_INDEX_NAME;
  }
};

/**
 * Normalizes the index name for use in a route object.
 *
 * @param uiState - The current UI state or a string representing the state.
 * @param addSortInfoToRouteProp - Optional flag to include sort information in the route.
 * @returns An object containing the normalized sortBy parameter and a flag indicating whether to add sortBy.
 */
const normalizeIndexNameForRoute = (
  uiState: string | RouteState,
  addSortInfoToRouteProp = false
): { sortBy?: string; willAddSortBy: boolean } => {
  const addSortInfoToRoute = !!addSortInfoToRouteProp;
  try {
    // If input is missing return undefined, otherwise trim and to-lower
    const check = typeof uiState === 'string' ? uiState : uiState[env.ALGOLIA_INDEX_NAME]?.sortBy;
    let input = check?.trim()?.toLowerCase();
    // If input is empty or equal to the search index, return undefined
    if (!input?.length || input === env.ALGOLIA_INDEX_NAME) {
      return {
        sortBy: '',
        willAddSortBy: false,
      };
    }
    // If input starts with searchindex_, trim
    if (input?.startsWith(env.ALGOLIA_INDEX_NAME)) {
      input = input?.substring(env.ALGOLIA_INDEX_NAME.length + 1);
    }
    return {
      sortBy: input ?? '',
      willAddSortBy: addSortInfoToRoute && !!input && !/_date_desc$/.exec(input),
    };
  } catch (error) {
    // Throwing an exception here will make algolia get stuck in an infinite loop, so safely handle it
    logError('AlgoliaSearch::stateToRoute', makeLoggableError({ error }));
    return {
      sortBy: '',
      willAddSortBy: false,
    };
  }
};

/**
 * Converts the given UI state for storage in a route object (query string parameters).
 *
 * @param uiState - The current UI state from Algolia.
 * @param addSortInfoToRoute - A flag indicating whether to include sort information in the route.
 * @returns An object representing the route with normalized query parameters.
 *
 * The function performs the following steps:
 * 1. Normalizes the `sortBy` parameter from the UI state.
 * 2. Extracts and normalizes various refinement lists from the UI state.
 * 3. Constructs and returns a route object with the normalized parameters.
 *
 * The returned route object includes:
 * - `query`: The normalized search query.
 * - `type`: The normalized type from the menu.
 * - `sortBy`: The normalized sortBy parameter (conditionally included).
 * - Various normalized refinement parameters.
 */
export const defaultStateToRoute = ({
  uiState,
  addSortInfoToRoute,
}: StateToRouteProps): RouteState => {
  const utmParams = getUtmParamsFromCurrentLocation();
  const searchIndex = env.ALGOLIA_INDEX_NAME;
  const { query, page, menu, refinementList } = uiState[searchIndex];
  const type = menu?.type;
  const { sortBy, willAddSortBy } = normalizeIndexNameForRoute(uiState, addSortInfoToRoute);
  const author = normalizeRefinementForRoute(refinementList?.author);
  const mediaType = normalizeRefinementForRoute(refinementList?.mediaType);
  const resourceType = normalizeRefinementForRoute(refinementList?.resourceType);
  const clinicalTopics = normalizeRefinementForRoute(refinementList?.clinicalTopics);
  const operationalTopics = normalizeRefinementForRoute(refinementList?.operationalTopics);
  const patientResources = normalizeRefinementForRoute(refinementList?.patientResources);
  const productLanguages = normalizeRefinementForRoute(refinementList?.productLanguages);
  const reportTypeFacet = normalizeRefinementForRoute(refinementList?.reportTypeFacet);
  const centerType = normalizeRefinementForRoute(refinementList?.centerType);
  const audience = normalizeRefinementForRoute(refinementList?.audience);
  const audiences = normalizeRefinementForRoute(refinementList?.audiences);
  const month = normalizeRefinementForRoute(refinementList?.month);
  const year = normalizeRefinementForRoute(refinementList?.year);
  const formats = normalizeRefinementForRoute(refinementList?.formats);
  const topics = normalizeRefinementForRoute(refinementList?.topics);
  const disease = normalizeRefinementForRoute(refinementList?.disease);
  const centerId = normalizeRefinementForRoute(refinementList?.centerId);
  return {
    ...utmParams,
    ...(query && { query: normalizeForRoute(query) }),
    ...(type && { type: normalizeForRoute(type) }),
    ...(willAddSortBy && { sortBy }),
    ...((page ?? 0) > 1 && { page: [page ?? 0] }),
    ...(centerId && { centerId }),
    author: normalizeForRoute(author),
    mediaType: normalizeForRoute(mediaType),
    resourceType: normalizeForRoute(resourceType),
    clinicalTopics: normalizeForRoute(clinicalTopics),
    operationalTopics: normalizeForRoute(operationalTopics),
    patientResources: normalizeForRoute(patientResources),
    productLanguages: normalizeForRoute(productLanguages),
    reportTypeFacet: normalizeForRoute(reportTypeFacet),
    centerType: normalizeForRoute(centerType),
    audience: normalizeForRoute(audience),
    audiences: normalizeForRoute(audiences),
    month: normalizeForRoute(month),
    year: normalizeForRoute(year),
    formats: normalizeForRoute(formats),
    topics: normalizeForRoute(topics),
    disease: normalizeForRoute(disease),
  } as RouteState;
};
/**
 * Converts a route state query string into a UI state object usable by Algolia UI
 * components.  This essentially revereses the logic within {@link stateToRoute}.
 *
 * @param routeState - The state object from the route, containing query parameters and other state information.
 * @returns A UI state object compatible with Algolia search.
 *
 * The function performs the following operations:
 * - Extracts `query`, `type`, and `txId` from the `routeState`.
 * - Determines the `sortBy` (active index) value based on the presence and normalization of `routeState.sortBy`.
 * - Constructs a `refinementList` from the `routeState`.
 * - Builds and returns a UI state object that includes the `query`, `sortBy`, and `refinementList` if they are present.
 * - Adds a `menu` object to the UI state if `type` or `txId` are present.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/ui-state/js/ for more details on the UI state structure.
 */
export const defaultRouteToState = ({
  routeState,
  addSortInfoToRoute,
  defaultSortByValue,
  defaultSortBySuffix,
  sortIndex,
}: RouteToStateProps<DefaultRouteQueryParameters>): RouteState => {
  const searchIndex = env.ALGOLIA_INDEX_NAME;
  const resolvedDefaultSortByValue = resolveDefaultSortByValue({
    defaultSortByValue,
    defaultSortBySuffix,
    sortIndex,
  });

  const refinementStateFromQuery = (queryParams: DefaultRouteQueryParameters) => {
    const {
      author,
      mediaType,
      resourceType,
      clinicalTopics,
      operationalTopics,
      patientResources,
      productLanguages,
      reportTypeFacet,
      centerType,
      audience,
      audiences,
      month,
      year,
      formats,
      topics,
      disease,
      centerId,
    } = queryParams;
    const refine = {
      author: splitRouteRefinement(author),
      mediaType: splitRouteRefinement(mediaType),
      resourceType: splitRouteRefinement(resourceType),
      clinicalTopics: splitRouteRefinement(clinicalTopics),
      operationalTopics: splitRouteRefinement(operationalTopics),
      patientResources: splitRouteRefinement(patientResources),
      productLanguages: splitRouteRefinement(productLanguages),
      reportTypeFacet: splitRouteRefinement(reportTypeFacet),
      centerType: splitRouteRefinement(centerType),
      audience: splitRouteRefinement(audience),
      audiences: splitRouteRefinement(audiences),
      month: splitRouteRefinement(month),
      year: splitRouteRefinement(year),
      formats: splitRouteRefinement(formats),
      topics: splitRouteRefinement(topics),
      disease: splitRouteRefinement(disease),
      centerId: splitRouteRefinement(centerId),
    };
    const cleanedRefine = Object.fromEntries(
      Object.entries(refine).filter(([, value]) => Array.isArray(value) && value.length > 0)
    ) as Record<string, string[]>;
    return Object.keys(cleanedRefine).length ? cleanedRefine : undefined;
  };
  const page = routeState.page ? Number.parseInt(String(routeState.page), 10) : undefined;

  const sortBy = normalizeIndexNameForState(routeState, resolvedDefaultSortByValue);
  const { query, type } = routeState;
  const refinementList = refinementStateFromQuery(routeState);
  // refer to uiState docs for details: https://www.algolia.com/doc/api-reference/widgets/ui-state/js/
  return {
    [searchIndex]: {
      ...(page && { page }),
      ...(query && { query }),
      ...(addSortInfoToRoute && !!sortBy && { sortBy }),
      ...(refinementList ? { refinementList } : {}),
      ...(type
        ? {
            menu: {
              ...(type && { type }),
            },
          }
        : {}),
    },
  };
};

export const tcDirectoryStateToRoute = ({
  uiState,
  addSortInfoToRoute,
}: StateToRouteProps): TcDirectoryQueryParameters => {
  const utmParams = getUtmParamsFromCurrentLocation();
  if (!uiState?.[env.ALGOLIA_INDEX_NAME]) {
    logWarning('Null state value detected in tcDirectoryStateToRoute');
    return {};
  }
  const searchIndex = env.ALGOLIA_INDEX_NAME;

  const { query: queryFromState, page: pageFromState, menu, refinementList } = uiState[searchIndex];
  const page = pageFromState ? Number.parseInt(String(pageFromState), 10) : undefined;
  const query = normalizeForRoute(queryFromState);
  const { sortBy, willAddSortBy } = normalizeIndexNameForRoute(uiState, addSortInfoToRoute);
  // Allowed search fields: sort, program, state, survivorship, txId, and query.
  const program = normalizeRefinementForRoute(refinementList?.programType);
  const state = normalizeRefinementForRoute(refinementList?.state);
  const survivorshipProgram = normalizeRefinementForRoute(refinementList?.survivorshipProgram);
  // Read txId from Algolia menu state, fall back to URL param.
  // The URL is the source of truth for txId since searchWithTransporter
  // injects it into every request directly.
  const menuTxId = normalizeForRoute(
    menu?.disease ?? (menu as { diseases?: string } | undefined)?.diseases
  );
  const txId =
    menuTxId ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('txId') ?? undefined
      : undefined);
  // Preserve sortBy: use the computed value if available, otherwise fall back to
  // the current URL sortBy (prevents sort from dropping on zero-result searches).
  // If the fallback is a dt_ sort but txId changed, update it to match the current txId.
  let urlSortBy =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('sortBy') ?? undefined
      : undefined;
  if (urlSortBy && txId && /^dt_.+_(asc|desc)$/.test(urlSortBy)) {
    const direction = urlSortBy.endsWith('_asc') ? 'asc' : 'desc';
    urlSortBy = `dt_${txId}_${direction}`;
  }
  const resolvedSortBy = willAddSortBy && !!sortBy ? sortBy : urlSortBy;

  return {
    ...utmParams,
    ...(query && { query: normalizeForRoute(query) }),
    ...((page ?? 0) > 1 && { page: [page ?? 0] }),
    ...(resolvedSortBy && { sortBy: resolvedSortBy }),
    ...(program && { program }),
    ...(state && { state }),
    ...(txId && { txId }),
    ...(survivorshipProgram && { survivorship: survivorshipProgram }),
  };
};

export const tcDirectoryRouteToState = ({
  routeState: {
    query,
    page: pageFromRoute,
    program: programFromRoute,
    survivorship: survivorshipFromRouteProp,
    suvivorship: survivorshipFromRoute,
    state: stateFromRoute,
    txId: txIdFromRoute,
    ...routeState
  },
  addSortInfoToRoute,
  defaultSortByValue,
  defaultSortBySuffix,
  sortIndex,
}: RouteToStateProps<TcDirectoryQueryParameters>): RouteState => {
  const searchIndex = env.ALGOLIA_INDEX_NAME;
  const resolvedDefaultSortByValue = resolveDefaultSortByValue({
    defaultSortByValue,
    defaultSortBySuffix,
    sortIndex,
  });
  const survivorshipFromQuery = survivorshipFromRouteProp ?? survivorshipFromRoute;

  const refineFromQuery = () => {
    const refine = {
      ...(programFromRoute && { programType: programFromRoute.split('|') }),
      ...(survivorshipFromQuery && { survivorshipProgram: survivorshipFromQuery.split('|') }),
      ...(stateFromRoute && { state: stateFromRoute.split('|') }),
    };
    return Object.values(refine).every((v) => !v?.length) ? {} : refine;
  };

  const page = pageFromRoute ? Number.parseInt(String(pageFromRoute), 10) : undefined;
  const sortBy = normalizeIndexNameForState(routeState, resolvedDefaultSortByValue);
  const refinementList = refineFromQuery();

  return {
    [searchIndex]: {
      configure: {
        filters: FILTERS_TCDIRECTORY,
      },
      ...(page && { page }),
      ...(query && { query }),
      ...(addSortInfoToRoute && !!sortBy && { sortBy }),
      ...(refinementList ? { refinementList } : {}),
      ...(txIdFromRoute
        ? {
            menu: {
              disease: txIdFromRoute,
            },
          }
        : {}),
    },
  };
};

/**
 * Checks if the given object is a TC Directory search state.
 *
 * @param check - The object to check.
 * @returns A boolean indicating whether the object is a TC Directory search state.
 */
export const isTcDirectorySearchState = (
  check: unknown
): check is NmdpUiTcDirectorySearchState | TypedRouteState<NmdpUiTcDirectorySearchState> => {
  if (typeof check !== 'object' || check == null) {
    return false;
  }
  const indexKey = env.ALGOLIA_INDEX_NAME as keyof typeof check;
  // Parse down to the index state and do a cast to partial to make the rest of the checks a little easier
  const work =
    indexKey in check && !!check[indexKey]
      ? (check[indexKey] as DeepPartial<NmdpUiTcDirectorySearchState>)
      : (check as DeepPartial<NmdpUiTcDirectorySearchState>);
  // expert-mode cheat-sheet: tc directory has a specific filter, we can just check for that
  return (work.configure?.filters ?? '') === FILTERS_TCDIRECTORY;
};

/**
 * Checks if the given object is a TC Directory search route.
 *
 * @param check - The object to check.
 * @returns A boolean indicating whether the object is a TC Directory search route.
 */
export const isTcDirectorySearchRoute = (check: unknown): check is TcDirectoryQueryParameters => {
  // Make sure we are working with an object
  if (typeof check !== 'object' || check == null) {
    return false;
  }
  // All of the TC Directory specific route parameters are unique to TC Dir, so if we find one of them
  // we are running a tc dir search....
  if ('state' in check || 'txId' in check || 'program' in check || 'survivorship' in check) {
    return true;
  }
  if ('suvivorship' in check) {
    return true;
  }
  // Otherwise, it's possible all the user has done is sorted.  Lets take a look at index values...
  if ('sortBy' in check && typeof check.sortBy === 'string') {
    const KnownTcDirSort = [
      'allos_asc',
      'allos_desc',
      'programType_asc',
      'programType_desc',
      'programtype_asc',
      'programtype_desc',
      'state_asc',
      'state_desc',
      'survivorship_asc',
      'survivorship_desc',
    ];
    return KnownTcDirSort.includes(check.sortBy.toLocaleLowerCase());
  }
  return false;
};

/**
 * Factory function to create a state mapping object for Algolia search routing.
 *
 * @param params - Configuration parameters for the state mapping.
 * @param params.type - The type of route state mapping to use.
 * @param params.addSortInfoToRoute - A flag indicating whether to include sort information in the route.
 * @returns A `StateMapping` object that defines how to map between UI state and route parameters.
 *
 * The returned `StateMapping` object contains two functions:
 * - `stateToRoute`: Converts the UI state to route parameters.
 * - `routeToState`: Converts route parameters back to UI state.
 *
 * If the `type` parameter is `RouteStateMapType.TcDirectory`, a specific mapping is applied.
 * Otherwise, a default mapping is used.
 */
export const routeStateMapperFactory = ({
  addSortInfoToRoute,
  defaultSortByValue,
  defaultSortBySuffix,
  sortIndex,
  useTcDirectoryRouteState,
}: RouteMapperOptions): StateMapping<RouteState, RouteQueryParameters> => {
  const resolvedDefaultSortByValue = resolveDefaultSortByValue({
    defaultSortByValue,
    defaultSortBySuffix,
    sortIndex,
  });
  /**
   * Maps a given `uiState` to a corresponding route representation based on its type.
   *
   * This function determines the type of the provided `uiState` and delegates
   * the mapping to the appropriate specialized function. If the `uiState` matches
   * a specific type (e.g., `TcDirectorySearchState`), it uses the specialized
   * mapping function for that type. For all other types, it falls back to a default
   * mapping function.
   *
   * @param uiState - The state object representing the current UI state to be mapped to a route.
   * @returns The route representation of the provided `uiState`.
   */
  const stateToRoute = (uiState: RouteState) => {
    if (useTcDirectoryRouteState) {
      return tcDirectoryStateToRoute({ uiState, addSortInfoToRoute });
    }
    if (isTcDirectorySearchState(uiState)) {
      return tcDirectoryStateToRoute({ uiState, addSortInfoToRoute });
    }
    // All other types (for now) are default.  At a bare minimum I'd like to get reports added to this
    return defaultStateToRoute({ uiState, addSortInfoToRoute });
  };

  /**
   * Maps a given route state to a specialized state object based on the route type.
   *
   * This function determines the type of the provided route state and delegates
   * the mapping to the appropriate handler. If the route state matches a specific
   * type (e.g., TC Directory Search), it uses the corresponding specialized mapper.
   * For all other route types, it falls back to a default mapping implementation.
   *
   * @param routeState - The route query parameters to be mapped.
   * @returns The mapped state object corresponding to the provided route state.
   */
  const routeToState = (routeState: RouteQueryParameters) => {
    if (useTcDirectoryRouteState) {
      return tcDirectoryRouteToState({
        routeState,
        addSortInfoToRoute,
        defaultSortByValue: resolvedDefaultSortByValue,
      });
    }
    if (isTcDirectorySearchRoute(routeState)) {
      return tcDirectoryRouteToState({
        routeState,
        addSortInfoToRoute,
        defaultSortByValue: resolvedDefaultSortByValue,
      });
    }
    // All other types (for now) are default.  At a bare minimum I'd like to get reports added to this
    return defaultRouteToState({
      routeState,
      addSortInfoToRoute,
      defaultSortByValue: resolvedDefaultSortByValue,
    });
  };
  return {
    stateToRoute,
    routeToState,
  };
};

/**
 * Factory function to create router properties for Algolia search integration.
 *
 * @param params - Configuration parameters for the router properties.
 * @param params.addSortInfoToRoute - A boolean indicating whether to include sorting information in the route state.
 * @returns An object containing the router and state mapping configuration.
 */
export const routerPropsFactory = ({
  addSortInfoToRoute,
  defaultSortByValue,
  defaultSortBySuffix,
  sortIndex,
  useTcDirectoryRouteState,
}: RouteMapperOptions): RouterProps<RouteState, RouteQueryParameters> => ({
  router: algoliaRouter,
  stateMapping: routeStateMapperFactory({
    addSortInfoToRoute,
    defaultSortByValue,
    defaultSortBySuffix,
    sortIndex,
    useTcDirectoryRouteState,
  }),
});

/**
 * Retrieves the current route state for an Algolia InstantSearch instance.
 *
 * This function reads the current UI state from the Algolia router and returns
 * it as a strongly-typed object. If no UI state exists or the state does not
 * match the expected index name, an empty object is returned.
 *
 * @template TStrongStateType - The type of the UI state for the Algolia index.
 *                              Defaults to `NmdpUiIndexState`.
 * @returns {TypedRouteState<TStrongStateType>} The strongly-typed UI state
 *                                              object for the Algolia index.
 */
export const getInstantSearchRouteState = <
  TStrongStateType extends NmdpUiIndexState = NmdpUiIndexState
>(): TypedRouteState<TStrongStateType> => {
  const uiState = algoliaRouter.read();
  return !!uiState && !!uiState[env.ALGOLIA_INDEX_NAME]
    ? (uiState as TypedRouteState<TStrongStateType>)
    : {};
};

/**
 * Retrieves the current route state for the Algolia search index,
 * mapped as a UI state for easy parsing.
 */
export const getInstantSearchUiStateFromRoute = <
  TStrongUiStateType extends NmdpUiIndexState = NmdpUiIndexState
>({
  addSortInfoToRoute,
  defaultSortByValue,
  defaultSortBySuffix,
  sortIndex,
  useTcDirectoryRouteState,
}: RouteMapperOptions): TStrongUiStateType => {
  const routeState = getInstantSearchRouteState();
  const mapper = routeStateMapperFactory({
    addSortInfoToRoute,
    defaultSortByValue,
    defaultSortBySuffix,
    sortIndex,
    useTcDirectoryRouteState,
  }).routeToState;
  return mapper(routeState) as unknown as TStrongUiStateType;
};
